import React, { useState, useEffect, useMemo } from "react";
import { Icon } from '@blueprintjs/core';
import 'react-edit-text/dist/index.css';
import style from "../../../../style.module.css";
import { useDevice } from "../../../../customHooks";
import Typography from "../../../../../core/Typography";
import iicon from '../../../../../assets/images/i-icon.png';
import Input from "../../../../../core/Inputs/DefaultInput";
import Svg from "../../../../../component/common/Svg";
import DataTable from "react-data-table-component";
import Callout from '../../../../../core/Callout';
import Card from "../../../../../core/Card";
import SubHeader from "../../../../../component/common/layout/SubHeader";
import VariableSidebar from "../../../../../component/common/layout/VariableSidebar";
import SurveyListSidebar from "../../../../../component/common/layout/SurveyListSidebar";
import SurveyListHeader from "../../../../../component/common/layout/SurveyListHeader";

import { useDispatch, useSelector } from "react-redux";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addCustomFormula, deleteCustomFormula, getCustomFormula, updateCustomFormula } from "../../../../../store/actions/variableAction";
import { useVerifySurveyId } from "../../../../../hooks";
import { Link } from "react-router-dom";
import { useRef } from "react";
import NestedMenuToggle from "../../../settings/NestedMenuToggle";
import { useTranslation } from 'react-i18next';

export default function CustomFormulas() {
  const { t, i18n } = useTranslation();
  //==> STATES <==

  //Pagination
  // const [perPage, setPerPage] = useState(10);
  // const [currentPage, setCurrentPage] = useState(1);

  //Modal
  const [popupTitle, setPopupTitle] = useState(t('survey.customFormula.add'));
  const [currentFormulaId, setCurrentFormulaId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  // const [cursorPosition, setCursorPosition] = useState(0);
  const [isVisibleModal, setIsVisibleModal] = useState(false)
  const [isHovering, setIsHovering] = useState(false);
  const [currentlyDeletingFormula, setCurrentlyDeletingFormula] = useState(null);
  const [nameError, setNameError] = useState(null);
  const formulaRef = useRef()

  //Selectors
  const customFormulas = useSelector((state) => state.variables?.customFormulaData)
  const customFormulasLoading = useSelector((state) => state.variables?.getCustomFormulaLoading)
  const { survey_name } = useSelector((state) => state.permissionsData)

  //Others
  let device = useDevice()
  const dispatch = useDispatch()
  const survey_id = useVerifySurveyId()
  const language_code = '22'
  


  //Columns
  const columns = useMemo(
    () => [
      {
        name: t('survey.customVariable.label'),
        cell: (row, index) => <span title={row.su_label}>{row.su_label ?? ''}</span>,
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_label ?? ''
          let b1 = b.su_label ?? ''
          return sortString(a1, b1)
        }
      },
      {
        name: t('survey.customFormula.name'),
        cell: (row, index) => <span title={row.su_name}>{row.su_name ?? ''}</span>,
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_name ?? ''
          let b1 = b.su_name ?? ''
          return sortString(a1, b1)
        }
      },
      {
        name: t('survey.customFormula.forName'),
        cell: (row, index) => {
          return (
            <>
              <span title={row.su_formula}> {row.su_formula ? `${row.su_formula.slice(0, 20)}...` : ''}</span >
              {/* <span title={JSON.parse(row.su_formula) && JSON.parse(row.su_formula)[language_code]}>{JSON.parse(row.su_formula) && (JSON.parse(row.su_formula)[language_code] ?? '')}</span> */}
            </>
          )
        },
        sortable: true,
        sortFunction: (a, b) => {
          let a1 = a.su_formula ?? ''
          let b1 = b.su_formula ?? ''
          return sortString(a1, b1)
        }
      },

      {
        name:t('survey.customVariable.action'),
        cell: (row, index) => <>

          <div className="d-flex action-buttons">
            {window.modulePermissions.M15.edit == 1 && (<button title="Edit" className="action-btn" type="button" aria-haspopup="true" aria-expanded="false" onClick={() => { modelEdit(row) }}>
              <Svg name="table-edit" />
            </button>)}
            {window.modulePermissions.M15.del == 1 && (
            <button title="Delete" className="action-btn" type="button" data-bs-toggle="modal" data-bs-target="#deleteFormulaModal" onClick={() => setCurrentlyDeletingFormula(row)} aria-haspopup="true" aria-expanded="false">
              <Svg name="table-delete" />
            </button>
            )}
          </div>
        </>
      }
    ]
  )


  //==> FORM SCHEMA AND VALIDATION <==

  //Yup - Schema
  const formSchema = Yup.object().shape({
    label: Yup.string().trim().required(t('survey.lRequired')),
    formulaName: Yup.string().trim().required(t('survey.NRequired')),
    formula: Yup.string().trim().required('Formula required'),
  })

  //Formik - Validation
  const initialValues = {
    label: '',
    formulaName: '',
    formula: ''
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: formSchema,
    onSubmit: (data) => {
      if (editMode) {
        submitEditFormula(data)
      }
      else {
        submitCreateFormula(data)
      }
    }
  })

  const { values, handleChange, handleBlur, touched, errors, resetForm, setValues, setFieldValue, setFieldTouched, handleSubmit } = formik

  const canMutate = editMode ? window.modulePermissions.M15.edit == 1 : window.modulePermissions.M15.add == 1

  //==> FUNCTIONALITIES <==

  //From submit
  const submitCreateFormula = (formData) => {
    const failCallback = (err) => {
      setNameError(err.name)
    }
    const payload = {
      su_label: formData.label,
      survey_id: survey_id,
      su_name: formData.formulaName,
      language_code: language_code,
      su_formula: formData.formula
    }
    dispatch(addCustomFormula(payload, resetModal, failCallback))
    fetchFormula()
  }

  const submitEditFormula = (formData) => {
    const payload = {
      su_custom_formula_id: currentFormulaId,
      su_label: formData.label,
      su_name: formData.formulaName,
      su_formulas: formData.formula,
      language_code: language_code
    }
    dispatch(updateCustomFormula(payload, resetModal))
    fetchFormula()
  }

  //Data handling
  const resetModal = (props) => {
    setIsVisibleModal(false)
    setCurrentFormulaId(null);
    setPopupTitle(t('survey.customFormula.add'))
    setEditMode(false);
    setNameError(null)
    resetForm()
    formulaRef.current.value = ''
  }

  const fetchFormula = async () => {
    dispatch(getCustomFormula(survey_id))
  }

  function modelEdit(formulaData) {
    setValues({
      label: formulaData.su_label,
      formulaName: formulaData.su_name,
      formula: formulaData.su_formula
      // formula: JSON.parse(formulaData.su_formula)[language_code]
    })
    formulaRef.current.value = formulaData.su_formula
    setCurrentFormulaId(formulaData.su_custom_formula_id)
    setPopupTitle( t('survey.customFormula.edit'))
    setEditMode(true)
    setIsVisibleModal(true)
  }

  function deleteFormula({ su_custom_formula_id }) {
    dispatch(deleteCustomFormula(su_custom_formula_id))
    setCurrentlyDeletingFormula(null)
  }

  //Form
  function getFormulaButtons(value) {
    if (!canMutate) return;
    const formulaSign = value;
    const formulaStr = formulaRef.current.value
    const cursorPosition = formulaRef.current.selectionStart
    formulaRef.current.value = formulaStr.slice(0, cursorPosition) + formulaSign + formulaStr.slice(cursorPosition)
    // setFieldValue('formula', formulaStr.slice(0, cursorPosition) + formulaSign + formulaStr.slice(cursorPosition))
    formulaRef.current.selectionStart = cursorPosition + 1
    formulaRef.current.selectionEnd = cursorPosition + 1
    formulaRef.current.focus()
  }

  //Tooltip
  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  }

  //Sorting
  const sortString = (a, b) => {
    a = a.toLowerCase()
    b = b.toLowerCase()

    if (a > b) {
      return 1;
    }
    if (b > a) {
      return -1;
    }
    return 0;
  }

  //==> EFFECTS <==

  useEffect(() => {
    if (survey_id) {
      fetchFormula()
    }
  }, [survey_id])

  useEffect(() => {
    setFieldValue('formula', formulaRef.current.value)
  }, [formulaRef?.current?.value])


  return (
    <div className='d-flex flex-column main-outer-wrapper main-site-wrapper language-page bg-white'>
      <SurveyListHeader />
      <div className="flex-grow-1 d-flex survey-main-wrapper">
        <SurveyListSidebar />

        <div id="content" className="flex-grow-1 dashboard-page-outer d-flex flex-column">
          <SubHeader />
          <div className="d-flex variable-pg-wrapper flex-grow-1">
            <VariableSidebar />
            <div className="flex-grow-1 variable-pg-content">
              <div className="survey-main-container">
                <div className="page-content-wrap variables-main-container p-0">
                  <div className="main-sec-wrap">
                    <div className='pagesRow varPg'>
                      <div className="page-heading">
                        {/* breadcrumbs here */}
                        <nav aria-label="breadcrumb">
                          <ol className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/admin/survey/list">{t('survey.title')}</Link></li>
                            <li className="breadcrumb-item"><Link to={`/admin/survey/create/qa-builder?survey_id=${survey_id}`}>{survey_name}</Link></li>
                            <li className="breadcrumb-item"><Link to={`/admin/survey/create/lead-gen-variables?survey_id=${survey_id}`}>{t('survey.variable.heading')}</Link></li>
                            <li className="breadcrumb-item active" aria-current="page"><strong>{t('survey.customFormula.custom_formula')}</strong></li>
                          </ol>
                        </nav>
                        {/* breadcrumbs ends here */}
                        <div className="d-flex align-items-start">
                          <div className="mt-2">
                            <NestedMenuToggle />
                          </div>
                          <div className="flex-grow-1 ps-3 ps-lg-0">
                            <h1 className="mb-0 mb-lg-3">{t('survey.customFormula.custom_formula')}</h1>
                            <p>{t('survey.customFormula.text1')}</p>
                          </div>
                        </div>
                      </div>
                      {/* <div className="leadGenvar">
                        <span className="me-2 mb-2">Results</span> =  <span className="ms-2 mb-2">Score.Q1</span> *3
                      </div> */}
                      <div className="varTable mt-4">
                        <div className="custom-data-table">
                          <DataTable
                            columns={columns}
                            data={customFormulas ?? []}
                            progressPending={customFormulasLoading}
                            pagination
                          // paginationServer
                          // paginationTotalRows={customFormulas ? customFormulas?.length : 0}
                          // paginationDefaultPage={currentPage}
                          // onChangeRowsPerPage={handlePerRowsChange}
                          // onChangePage={handlePageChange}
                          // selectableRows
                          // onSelectedRowsChange={({ selectedRows }) => console.log(selectedRows)}
                          />
                        </div>
                        {window.modulePermissions.M15.add == 1 && (<button type="button" className="btn btn-primary add-btn" onClick={() => setIsVisibleModal(true)} srno="1" value="1">
                          <Svg name="plus" /> {t('survey.customFormula.add_New_formula')} </button>)}
                        <div className={`customvar-modal modal fade edit-pop-modal ${isVisibleModal ? 'show' : ''}`} style={{ display: `${isVisibleModal ? 'block' : 'none'}` }} id="editPins" tabIndex={-1} aria-labelledby="editModalLabel" aria-hidden="true">
                          <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                              <div className="modal-body">
                                <div className={"formcontainer" + " "}>
                                  <form onSubmit={(e) => { if (!canMutate) { e.preventDefault(); return; } handleSubmit(e); }}>
                                    <div className={"formvawrapper" + " " + style.wrapper}>
                                      <Card view="raised" className={"formstyle p-4" + " " + style.card} >
                                        <Typography
                                          type={device === "mobile" ? "h6" : "h5"}
                                          colorStep={100}
                                          tagName="div"
                                          className={"formhead "}
                                        >
                                          {/* Add custom formula */}
                                          {popupTitle}
                                        </Typography>
                                        <div className={"mt-5"}>
                                          <div className="row">
                                            <div className="col-md-6">
                                              <div className="form-group">
                                                <label htmlFor="">{t('survey.customVariable.label')}<span className="text-primary">*</span></label>
                                                <div className="position-relative">
                                                  <Input
                                                    type="text"
                                                    view="outlined"
                                                    color="default"
                                                    label=""
                                                    fill
                                                    name='label'
                                                    className={"formsinput" + " " + style.card__exp}
                                                    placeholder={editMode ? values.label : t('survey.customVariable.label')}
                                                    value={values.label}
                                                    readOnly={!canMutate}
                                                    onChange={canMutate ? handleChange : undefined}
                                                    onBlur={handleBlur}
                                                    onClear={canMutate ? () => setFieldValue('label', '') : undefined}
                                                  />
                                                  {touched.label && errors.label && <div className='validation-msg'>{errors.label}</div>}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="col-md-6">
                                              <div className="input_with_info form-group">
                                                <label htmlFor="">{t('survey.customFormula.name')}<span className="text-primary">*</span></label>
                                                <div className={"inputtext position-relative"}>
                                                  <Input
                                                    type="text"
                                                    view="outlined"
                                                    color="default"
                                                    label=""
                                                    fill
                                                    className={"formsinput" + " " + style.card__exp}
                                                    name="formulaName"
                                                    placeholder={editMode ? values.formulaName : t('survey.customFormula.name')}
                                                    value={values.formulaName}
                                                    readOnly={!canMutate}
                                                    onChange={canMutate ? handleChange : undefined}
                                                    onBlur={handleBlur}
                                                    onClear={canMutate ? () => setFieldValue('formulaName', '') : undefined}
                                                  />
                                                  {touched.formulaName && errors.formulaName && <div className='validation-msg'>{errors.formulaName}</div>}
                                                  {nameError && <div className='validation-msg'>{nameError}</div>}
                                                  {/* <div className="tooltipInit" onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}><img src={iicon} alt="info" />   {isHovering && (
                                                    <div className='infobox callout-right'>
                                                      < Callout
                                                        type='def'
                                                        view='outlined'
                                                        color='primary'
                                                        title='Unique identifier'
                                                        icon={< Icon icon='info-sign' iconSize={24} />}
                                                      >
                                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum eleifend arcu laoreet elit maximus, eu accumsan tortor suscipit.
                                                      </Callout >

                                                    </div>
                                                  )}</div> */}
                                                </div>
                                              </div>
                                            </div>
                                          </div>



                                        </div>
                                        <div className="input_with_info">
                                          <div className="btn-leftText mb-4 mt-4 ">{t('survey.customFormula.type_symbols')}
                                            <span className="btns-grp me-1 ms-1">
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('x')} className="smallbtn">&#123;x&#125;</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('+')} className="smallbtn">+</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('-')} className="smallbtn">-</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('*')} className="smallbtn">*</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('/')} className="smallbtn">/</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('{')} className="smallbtn">&#123;</button>
                                              <button type="button" disabled={!canMutate} onClick={() => getFormulaButtons('}')} className="smallbtn">&#125;</button>
                                            </span> {t('survey.customFormula.with_variables')}
                                          </div>
                                        </div>
                                        <div className="form-group">
                                          <label htmlFor=""> {t('survey.customFormula.forName')}<span className="text-primary">*</span></label>
                                          <div className={"textareaF position-relative"}>
                                            <textarea
                                              id="text1"
                                              cols="1"
                                              rows="3"
                                              name="formula"
                                              readOnly={!canMutate}
                                              ref={formulaRef}
                                              placeholder={editMode ? values.formula : t('survey.customFormula.enterFormula')}
                                              onBlur={handleBlur}
                                            >

                                            </textarea>
                                            {touched.formula && errors.formula && <div className='validation-msg'>{errors.formula}</div>}
                                          </div>

                                        </div>
                                        <div className="button-wrap d-flex justify-content-end">
                                          <button type="button" className="btn btn-bordered" onClick={resetModal}>{t('survey.customVariable.cancel')}</button>
                                          <button type="submit" className="btn btn-primary ms-3" disabled={!canMutate}>
                                            {editMode ? t('survey.customFormula.update') : t('survey.customFormula.add')}
                                          </button>
                                        </div>
                                      </Card>
                                    </div >
                                  </form>
                                </div >
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="customvar-modal modal custom-modal fade deleteModal" id="deleteFormulaModal" tabIndex={-1} aria-labelledby="deleteModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className={"formcontainer" + " " + style.container}>
                <div className={"formvawrapper delete-modal-wrap modal-sm" + " " + style.wrapper}>
                  <Card view="raised" className={"formstyle p-0" + " " + style.card} >

                    <div className="delete-body-wrap delete-language">
                      <div className="heading">
                        <h3> {t('survey.customFormula.delete')}</h3>
                        <p>{t('survey.customVariable.del1')} <strong>{currentlyDeletingFormula?.su_name ?? ''}</strong>  {t('survey.customVariable.permanently')} </p>
                      </div>

                      <div className="button-wrap d-flex justify-content-end">
                        <button type="button" className="btn btn-bordered" data-bs-dismiss="modal" onClick={() => setCurrentlyDeletingFormula(null)}>{t('survey.customVariable.cancel')}</button>
                        {window.modulePermissions.M15.del == 1 && (<button type="button" className="btn btn-delete ms-3" data-bs-dismiss="modal" onClick={() => { deleteFormula(currentlyDeletingFormula) }}>{t('survey.customVariable.confirm_delete')}</button>)}
                      </div>
                    </div>
                  </Card>
                </div >
              </div >
            </div>
          </div>
        </div>
      </div>

      <div className={`modal-backdrop fade ${isVisibleModal ? 'show' : ''}`} style={{ display: `${isVisibleModal ? 'block' : 'none'}` }}></div>
    </div>
  )
}
